<template>
  <div class="bankid-ssn-container">
    <FriedInput
      v-model="ssn"
      data-external="bankid-person-number-input"
      class="bankid-input"
      :disabled="isLoading"
      :label="$t('your-personal-number-yyyy-mm-dd')"
      :error-message="showInvalidSsnText ? $t('enter-a-valid-personal-identity-number') : ''"
    />
    <FriedButton
      id="sendButton"
      data-external="bankid-sign-button"
      :loading="isLoading"
      :disabled="!validSsn"
      @click="handleStartSSN"
    >
      <span>{{ $t('bankid-sign') }}</span>
    </FriedButton>
    <FriedButton
      v-if="bankId.ssn"
      data-external="bankid-sign-cancel-button"
      :button-type="ButtonType.Secondary"
      @click="handleCancel"
    >
      {{ $t('cancel') }}
    </FriedButton>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { watch, onMounted, computed, defineComponent, ref } from 'vue';
import { ButtonType } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { SigningHelper } from '../../../helpers/signing.helper';
import type { BankId } from '../../../types/bankid';
import { BankIdStep, HintCodePending } from '../../../types/enums/bankid-se';

export default defineComponent({
  props: {
    bankId: {
      type: Object as PropType<BankId>,
      required: true,
    },
  },
  emits: ['cancel', 'startSSN'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const ssn = ref('');
    const startValidate = computed(() => ssn.value.length >= 10);
    const validSsn = computed(() => SigningHelper.validateSsn(ssn.value));
    const showInvalidSsnText = computed(() => !validSsn.value && startValidate.value);
    const isLoading = computed(() => props.bankId.step === BankIdStep.Polling);
    const descriptionText = computed(() =>
      props.bankId.hintCode === HintCodePending.userSign
        ? `${t('waiting-for-signature')}. `
        : t('start-your-bankid-app-or').toString()
    );

    const handleCancel = () => {
      emit('cancel', ssn.value);
    };

    const handleStartSSN = () => {
      emit('startSSN', ssn.value);
    };

    const setSsn = () => {
      if (props.bankId.ssn) {
        ssn.value = props.bankId.ssn;
      }
    };

    watch(
      () => props.bankId.ssn,
      () => {
        setSsn();
      }
    );

    onMounted(() => {
      setSsn();
    });

    return {
      ssn,
      startValidate,
      validSsn,
      showInvalidSsnText,
      isLoading,
      descriptionText,
      ButtonType,
      handleCancel,
      handleStartSSN,
      setSsn,
    };
  },
});
</script>
<style lang="scss" scoped>
.bankid-ssn-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-50);

  .bankid-input {
    width: 100%;
  }
}
</style>
